import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setActionBox } from '../slices/actionBoxSlice'
import { generateRESTQuery, getBaseQuery } from './apiUtils'
import apiConstants, { HTTP_METHODS } from './constants'

export const actionBoxesApi = createApi({
  reducerPath: 'actionBoxesApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'ActionBoxList',
    'ActionBoxDetails',
    'AllActionBoxes',
    'ActionBoxGroups'
  ],
  endpoints: (builder) => ({
    getActionBoxes: builder.query<
      {
        items: ActionBox[]
        pagination: CommonPaginationPayload
      },
      CommonSearchParams
    >({
      query: (requestParams: CommonSearchParams) =>
        generateRESTQuery({
          path: apiConstants.ACTION_BOXES_REST_URL,
          queryParams: requestParams
        }),
      providesTags: ['ActionBoxList']
    }),
    getActionBoxDetails: builder.query<ActionBox, { ActionBoxID: number }>({
      query: (requestParams: { ActionBoxID: number }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/${requestParams.ActionBoxID}`
        }),
      providesTags: ['ActionBoxDetails'],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: ActionBox
          }
          if (data) {
            dispatch(setActionBox(data))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    validateActionBoxSteps: builder.mutation<
      CommonValidationResponse,
      ActionBoxValidationParams
    >({
      query: (requestParams: ActionBoxValidationParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/validate-steps`,
          method: HTTP_METHODS.POST,
          body: requestParams
        })
    }),
    getAllActionBoxes: builder.query<ActionBox[], { TenantID: string }>({
      query: (requestParams: { TenantID: string }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/ui-positions`,
          queryParams: requestParams
        }),
      providesTags: ['AllActionBoxes']
    }),
    createActionBox: builder.mutation<number, ActionBoxCreateParams>({
      query: (requestParams: ActionBoxCreateParams) =>
        generateRESTQuery({
          path: apiConstants.ACTION_BOXES_REST_URL,
          method: HTTP_METHODS.POST,
          body: requestParams
        }),
      invalidatesTags: ['ActionBoxDetails', 'ActionBoxList', 'AllActionBoxes']
    }),
    editActionBox: builder.mutation<number, ActionBoxCreateParams>({
      query: (body: ActionBoxCreateParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}`,
          method: HTTP_METHODS.PUT,
          body
        }),
      invalidatesTags: ['ActionBoxDetails', 'ActionBoxList', 'AllActionBoxes']
    }),
    getActionBoxTargetGroups: builder.query({
      query: (requestParams: { ActionBoxID: number; isExcluded: boolean }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/segments/${requestParams.ActionBoxID}`,
          queryParams: { isExcluded: requestParams.isExcluded }
        }),
      providesTags: ['ActionBoxGroups']
    }),
    addActionBoxToGroup: builder.mutation({
      query: (body: {
        ActionBoxID: number
        SegmentID: number
        Excluded: boolean
      }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/segments`,
          method: HTTP_METHODS.POST,
          body
        }),
      invalidatesTags: ['ActionBoxGroups']
    }),
    removeActionBoxFromGroup: builder.mutation({
      query: (body: { ActionBoxID: number; SegmentID: number }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/segments`,
          method: HTTP_METHODS.DELETE,
          body
        }),
      invalidatesTags: ['ActionBoxGroups']
    }),
    updateActionBoxAvailability: builder.mutation({
      query: (body: { ActionBoxID: number; AvailableToAll: boolean }) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/${body.ActionBoxID}/availability`,
          method: HTTP_METHODS.PATCH,
          body: {
            AvailableToAll: body.AvailableToAll
          }
        }),
      invalidatesTags: ['ActionBoxGroups', 'ActionBoxDetails']
    }),
    updateActionBoxUiPositions: builder.mutation({
      query: (body: UpdateActionBoxUIsParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/ui-positions`,
          method: HTTP_METHODS.PUT,
          body
        })
    })
  })
})

export const {
  useGetActionBoxesQuery,
  useGetActionBoxDetailsQuery,
  useValidateActionBoxStepsMutation,
  useGetAllActionBoxesQuery,
  useCreateActionBoxMutation,
  useEditActionBoxMutation,
  useGetActionBoxTargetGroupsQuery,
  useAddActionBoxToGroupMutation,
  useRemoveActionBoxFromGroupMutation,
  useUpdateActionBoxAvailabilityMutation,
  useUpdateActionBoxUiPositionsMutation
} = actionBoxesApi
